import type { MentionItem } from 'react-mentions';
import { trimStart } from 'lodash-es';

import type { User } from '@hotjar/user';
import type { ContentToken } from '../domains/model';

export type IndexedMentionItem = MentionItem & { index: number };

export type DetailedIndexedMentionItem = IndexedMentionItem & Omit<User, 'id'>;

export const MENTION_TRIGGER = '@';

export function createMentionMarkup(id: string, display: string): string {
  // NOTE: mention trigger has to be the first character of the markup
  // otherwise the experience will be broken when you try to type the second character
  // ("[" here) BEFORE some already added mention
  return `${MENTION_TRIGGER}[${display}](${id})`;
}

export function parse(
  text: string,
  mentions: Array<DetailedIndexedMentionItem>,
): Array<ContentToken> {
  const mentionsCopy = mentions.slice();
  const tokens: Array<ContentToken> = [];
  let lastIndex = 0;

  // FIXME: no-constant-condition
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const mention = mentionsCopy.shift();

    if (!mention) {
      break;
    }

    const textTokenValue = text.substring(lastIndex, mention.index);
    if (textTokenValue) {
      tokens.push({
        type: 'text',
        value: textTokenValue,
      });
    }

    /**
     * Trim is needed since the parse output is not consistent in these cases:
     * 1) Submit the comment and edit it (without refresh)
     * 2) Submit the comment, then refresh the page, then edit the comment
     * in the first case the @ will get doubled
     */
    const display = trimStart(mention.display, MENTION_TRIGGER);

    tokens.push({
      type: 'mention',
      value: {
        id: Number(mention.id),
        name: display,
        email: mention.email || null,
        avatar: mention.avatar || null,
      },
    });

    lastIndex = mention.index + createMentionMarkup(mention.id, display).length;
  }

  const lastTokenValue = text.substring(lastIndex);
  if (lastTokenValue) {
    tokens.push({
      type: 'text',
      value: lastTokenValue,
    });
  }

  return tokens;
}

export function stringify(tokens: Array<ContentToken>): [string, Array<IndexedMentionItem>] {
  return tokens.reduce(
    (
      [text, mentions]: [string, Array<IndexedMentionItem>],
      token: ContentToken,
    ): [string, Array<IndexedMentionItem>] => {
      switch (token.type) {
        case 'text':
          return [text + token.value, mentions];
        case 'mention': {
          const { id: userId, name: userName } = token.value;
          const mentionItem = {
            id: String(userId),
            display: userName,
            index: text.length,
          } as IndexedMentionItem;
          const mentionMarkup = createMentionMarkup(String(userId), userName);

          return [text + mentionMarkup, [...mentions, mentionItem]];
        }
        default:
          return [text, mentions];
      }
    },
    ['', []],
  );
}

export function resolveMentionDetails(
  mentions: Array<IndexedMentionItem>,
  users: Array<User>,
): Array<DetailedIndexedMentionItem> {
  const usersMap = users.reduce(
    (map, user) => ({
      ...map,
      [String(user.id)]: user,
    }),
    {} as { [k: string]: User },
  );

  return mentions.map((mention) => ({
    ...(usersMap[mention.id] || {
      name: mention.display,
      email: null,
      avatar: null,
    }),
    ...mention,
  }));
}
